import { gql } from "@apollo/client";
import { useEffect } from "react";

import { useCurrentAccountId } from "@zapier/identity";

import { MemberRole } from "../graphql-types";
import { Range } from "../types";
import {
  AccountLimitsFragment,
  CurrentAccountPlanFragment,
  useAccountAndUserQuery,
} from "./useAccountAndUser.generated";
import { useHasHydrated } from "./useHasHydrated";
import { useSessionStorage } from "./useSessionStorage";

export type BillingPeriod = Partial<Range<string>>;

interface OptionalAccountAndUserFields {
  accountId: string;
  allowImportExport: boolean;
  allowProfessional: boolean;
  allowTaskReplay: boolean;
  currentUsageCycle: Range<string>;
  dataRetentionDays?: number;
  email: string;
  fullName: string;
  isFree: boolean;
  isStaff: boolean;
  isTeam: boolean;
  limits: AccountLimitsFragment;
  plan: CurrentAccountPlanFragment;
  role: MemberRole;
  userId: string;
  parentAccount: {
    id: string;
    name: string;
  } | null;
  parentAccountRelationship: string | null;
}

type PendingAccountAndUser = {
  isLoading: true;
  currentBillingPeriod: BillingPeriod;
} & Partial<OptionalAccountAndUserFields>;

type LoadedAccountAndUser = {
  isLoading: false;
  currentBillingPeriod: BillingPeriod;
} & OptionalAccountAndUserFields;

export type AccountAndUser = PendingAccountAndUser | LoadedAccountAndUser;

export const accountAndUserQuery = gql`
  fragment CurrentAccountPlan on Plan {
    allowImportExport
    allowProfessional
    hasMeteredBilling
    hasTeams
    id
    meteredPricingTiers {
      limit
      perTask
    }
    name
    planType
    tasks
  }

  fragment AccountLimits on Entitlements {
    isAlertsCenterEnabled
    isTaskUsageAlertsEnabled
    isAnalyticsDashboardEnabled
    isCustomNotificationsEnabled
    isLogStreamsEnabled
    isWorkspacesEnabled
    rolloutStage
  }

  query AccountAndUser($accountId: ID!) {
    account(id: $accountId) {
      allowTaskReplay
      currentBillingCycle {
        from
        to
      }
      currentUsageCycle {
        from
        to
      }
      currentUserRole
      id
      isPaying
      dataRetentionDays
      entitlements {
        ...AccountLimits
      }
      plan {
        ...CurrentAccountPlan
      }
      parentAccount {
        id
        name
      }
      parentAccountRelationship
    }
    currentUser {
      email
      fullName
      id
      isStaff
    }
  }
`;

export function useAccountAndUser(): AccountAndUser {
  const accountId = useCurrentAccountId();
  const hasHydrated = useHasHydrated();

  const { data, loading } = useAccountAndUserQuery({
    skip: !accountId,
    variables: { accountId: accountId?.toString() ?? "" },
  });

  const [cachedAccount, setCachedAccount] =
    useSessionStorage<AccountAndUser>("reportingAccount");
  const currentAccountId = accountId?.toString();

  useEffect(() => {
    if (
      !hasHydrated ||
      !cachedAccount ||
      cachedAccount.accountId === currentAccountId
    ) {
      return;
    }

    // Account context can change without ending the browser session. Remove
    // the previous account's data before downstream telemetry can reuse it.
    setCachedAccount(undefined);
  }, [cachedAccount, currentAccountId, hasHydrated, setCachedAccount]);

  useEffect(() => {
    if (loading || !accountId || !data?.account || !data?.currentUser) {
      return;
    }

    const account: AccountAndUser = {
      accountId: accountId.toString(),
      allowImportExport: data.account.plan.allowImportExport,
      allowProfessional: data.account.plan.allowProfessional,
      allowTaskReplay: data.account.allowTaskReplay,
      currentBillingPeriod: data.account.currentBillingCycle,
      currentUsageCycle: data.account.currentUsageCycle,
      dataRetentionDays: data.account.dataRetentionDays,
      email: data.currentUser.email,
      fullName: data.currentUser.fullName,
      limits: data.account.entitlements,
      role: data.account.currentUserRole,
      isFree: !data.account.isPaying,
      isLoading: loading,
      isStaff: data.currentUser.isStaff,
      isTeam: data.account.plan.hasTeams,
      parentAccount: data.account.parentAccount || null,
      parentAccountRelationship: data.account.parentAccountRelationship || null,
      plan: data.account.plan,
      userId: data.currentUser.id,
    };

    setCachedAccount(account);
  }, [accountId, data, loading, setCachedAccount]);

  if (
    hasHydrated &&
    cachedAccount &&
    cachedAccount.accountId === currentAccountId
  ) {
    return cachedAccount;
  }

  return { currentBillingPeriod: {}, isLoading: true };
}
